import { cn } from '@veem/ui'
import { For, type JSX, Show } from 'solid-js'

export const ViewSelector = <T extends string>(props: {
  label: string
  value: T
  /** An option with an icon shows only the icon; its label stays as the accessible name. */
  options: readonly { key: T; label: string; count?: number; icon?: JSX.Element }[]
  onChange: (key: T) => void
}) => (
  <div
    role="tablist"
    aria-label={props.label}
    class="inline-flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-[0.625rem] bg-surface-sunken p-0.5"
  >
    <For each={props.options}>
      {(item, index) => (
        <button
          type="button"
          role="tab"
          aria-selected={props.value === item.key}
          aria-label={item.icon ? item.label : undefined}
          title={item.icon ? item.label : undefined}
          tabIndex={props.value === item.key ? 0 : -1}
          onClick={() => props.onChange(item.key)}
          onKeyDown={(event) => {
            const length = props.options.length
            const next =
              event.key === 'ArrowRight'
                ? (index() + 1) % length
                : event.key === 'ArrowLeft'
                  ? (index() + length - 1) % length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? length - 1
                      : null
            if (next === null) return
            event.preventDefault()
            const option = props.options[next]
            if (!option) return
            props.onChange(option.key)
            const buttons =
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role=tab]')
            buttons?.[next]?.focus()
          }}
          class={cn(
            'flex h-7 shrink-0 touch-manipulation items-center gap-1.5 rounded-[0.5rem] px-2.5 text-body font-medium transition-[background-color,color,box-shadow] duration-(--duration-press) focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent motion-reduce:transition-none max-md:h-(--size-touch)',
            props.value === item.key
              ? 'bg-surface text-text shadow-[0_0_0_1px_var(--color-border),0_1px_2px_oklch(0.196_0.007_265/0.06)]'
              : 'text-text-muted hover:text-text',
          )}
        >
          <Show when={item.icon} fallback={item.label}>
            <span class="grid size-4 place-items-center [&_svg]:size-4" aria-hidden="true">
              {item.icon}
            </span>
          </Show>
          <Show when={item.count !== undefined}>
            <span class="font-mono text-micro text-text-subtle tabular-nums">{item.count}</span>
          </Show>
        </button>
      )}
    </For>
  </div>
)
